<template>
    <div>

        <vue-headful :title="'Training Course Matrix :: MeritEHS'"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/training-courses`">
                        <i class="fa fa-graduation-cap"></i> Training Courses
                    </router-link>
                </li>
                <li class="breadcrumb-item active">
                    Matrix
                </li>
            </ol>
        </h4>

        <div class="mb-3">
            <div class="form-inline">
                <div class="form-group mr-2">
                    <button class="btn btn-default" v-on:click="show_filters = !show_filters">
                        <i class="fa fa-filter"></i>
                        <span class="badge" style="background-color: #d9534f" :title="`${filterCount} Filter(s) Applied`" v-if="filterCount > 0">{{ filterCount }}</span>
                    </button>
                </div>
                <div class="form-group mr-2">
                    <button class="btn btn-warning" v-on:click="getMatrix(false)" :disabled="loading">
                        <template v-if="loading">
                            <i class="fa fa-spinner fa-spin fa-lg"></i> Building Matrix
                        </template>
                        <template v-else>
                            <i class="fa fa-filter"></i> Build Matrix
                        </template>
                    </button>
                </div>
                <div class="form-group mr-2">
                    <button class="btn btn-success" v-on:click="getMatrix(false, true)" :disabled="loading">
                        <template v-if="loading">
                            <i class="fa fa-spinner fa-spin fa-lg"></i> Building Matrix
                        </template>
                        <template v-else>
                            <i class="fa fa-file-excel-o"></i> Export
                        </template>
                    </button>
                </div>
            </div>

            <div v-show="show_filters" class="mt-2 card">
                <div class="card-body">
                    <div class="form-inline">
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.sort_by"
                                :btnLabel="(values) => ('Sort By ('+ (values[0] ? values[0].name : '')+')')"
                                :selectOptions="sortByOptions"
                                :options="{multi: false}" />
                        </div>
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.display_trade_or_position"
                                :btnLabel="(values) => ('Trade/Position Display ('+ (values[0] ? values[0].name : '')+')')"
                                :selectOptions="tradeOrPositionOptions"
                                :options="{multi: false}" />
                        </div>
                    </div>
                    <div class="mt-2 form-inline">
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.division_id" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisionOptions" :options="{multi: true}"></multi-select>
                        </div>
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.trade" :btnLabel="(values) => ('Trades ('+values.length+')')" :selectOptions="tradeOptions" :options="{multi: true}"></multi-select>
                        </div>
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.positions" :btnLabel="(values) => ('Position ('+values.length+')')" :selectOptions="positionOptions" :options="{multi: true}"></multi-select>
                        </div>
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.training_course_id" :btnLabel="(values) => ('Courses ('+values.length+')')" :selectOptions="courseOptions" :options="{multi: true}"></multi-select>
                        </div>
                        <div class="form-group mr-2">
                            <multi-select v-model="filters.training_catalog_id" :btnLabel="(values) => ('Catalogs ('+values.length+')')" :selectOptions="catalogOptions" :options="{multi: true}"></multi-select>
                        </div>
                        <div class="form-group mr-2">
                            <date-range-selector buttonTitle="Date Completed" v-model="filters.completed_at" />
                        </div>
                        <div class="form-group mr-2">
                            <date-range-selector buttonTitle="Date Expires" v-model="filters.expires_at" />
                        </div>
                    </div>

                    <div class="mt-2 form-inline">
                        <div class="form-group mr-2">
                            <b-form-select v-model="filters.date_display" :options="dateDisplayOptions" size="sm"></b-form-select>
                        </div>
                        <div class="form-group mr-2">
                            <b-form-select v-model="filters.IsActive" :options="activeOptions" size="sm"></b-form-select>
                        </div>
                        <div class="form-group mr-2">
                            <b-form-input v-model="filters.employee_number" size="sm" placeholder="Filter Employee Number"></b-form-input>
                        </div>
                        <div class="form-group mr-2">
                            <b-form-input v-model="filters.last_name" size="sm" placeholder="Filter Employee Last Name"></b-form-input>
                        </div>

                    </div>

                    <div class="mt-2 form-inline">
                        <div class="form-group mr-2">
                            <b-form-select v-model="filters.completed_at_least_one" :options="completedTypeOptions" size="sm"></b-form-select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card">
            <div class="card-body">
                <template v-if="!has_ran">
                    <div class="alert alert-warning">Select Filters and Click Build Matrix to Begin</div>
                </template>
                <template v-else>
                    <div v-if="loading">
                        <i class="fa fa-spinner fa-spin fa-lg"></i> Building Matrix
                    </div>
                    <div class="table-responsive" v-else>
                        <b-table :sticky-header="(tableHeight ? tableHeight : 600) + 'px'" :bordered="true" :no-border-collapse="true" responsive :items="items" :fields="fields">
                            <template v-slot:cell(LastName)="data">
                                {{ data.item.LastName }}, {{ data.item.FirstName }}
                            </template>
                        </b-table>
                    </div>
                </template>
            </div>
            <div class="card-footer" v-if="has_ran && !loading">
                <div class="form-inline">
                    <label>Matrix Height:</label>
                    &nbsp;<b-form-input type="number" v-model="tableHeight" size="sm" placeholder="Enter Matrix Height" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                loading: false,
                has_ran: false,
                show_filters: false,
                divisions: [],
                positions: [],
                employees: [],
                courses: [],
                courseFilters: [],
                catalogFilters: [],
                catalogs: [],
                trades: [],
                filters: {
                    sort_by: [{id: 'employee.LastName', name: 'Employee Last Name'}],
                    display_trade_or_position: [{id: 'trade', name: 'Trade'}],
                    division_id: null,
                    trade: null,
                    training_course_id: null,
                    employee_number: null,
                    last_name: null,
                    completed_at_least_one: null,
                    IsActive: null,
                    date_display: null,
                    completed_at: null,
                    expires_at: null,
                    positions: null,
                },
                tableHeight: 600,
            }
        },

        computed: {
            filterCount(){
                //Setup
                var count = 0;
                var keys = Object.keys(this.filters);

                //Loop and build
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    //Check arrays
                    if(Array.isArray(this.filters[keys[i]]))
                    {
                        //Check empty
                        if(this.filters[keys[i]].length > 0)
                        {
                            count++;
                        }
                    }
                    else if(this.filters[keys[i]] != null)
                    {
                        count++;
                    }
                }

                //SEnd it
                return count;
            },

            fields(){
                //Setup
                var fields = [
                    // { key: 'LastName', stickyColumn: true, label: 'Employee#/Name/Trade', variant: 'success'},
                    { key: 'EmployeeNumber', stickyColumn: true, label: 'Employee#', variant: 'success'},
                ];

                //Check Trade/Position Display
                if(this.filters.display_trade_or_position && this.filters.display_trade_or_position[0])
                {
                    //Push the Dynamic Field
                    fields.push({
                        key: this.filters.display_trade_or_position[0].id,
                        label: this.filters.display_trade_or_position[0].name,
                        stickyColumn: true,
                        variant: 'success',
                    });
                }
                else
                {
                    fields.push({ key: 'trade', stickyColumn: true, label: 'Trade', variant: 'success'});
                }

                //Push Other Columns
                fields.push({ key: 'division_code_name', stickyColumn: true, label: 'Division', variant: 'success'});
                fields.push({ key: 'LastName', stickyColumn: true, label: 'Name', variant: 'success'});

                //Build the courses
                var courseKeys = Object.keys(this.courses);

                //Setup
                for(var i = 0; i < courseKeys.length; i++)
                {
                    fields.push(this.courses[courseKeys[i]].course_name);
                }

                //Build it
                return fields;
            },

            items(){
                //Setup
                var items = [];

                //Build the courses
                var courseKeys = Object.keys(this.courses);

                //Setup
                for(var i = 0; i < this.employees.length; i++)
                {
                    //Build the employee
                    var emp = {
                        EmployeeNumber: this.employees[i].EmployeeNumber,
                        LastName: this.employees[i].LastName,
                        FirstName: this.employees[i].FirstName,
                        trade: this.employees[i].trade,
                        position: this.employees[i].position,
                        division_code_name: this.employees[i].division_code_name,
                    };

                    //Loop
                    for(var j = 0; j < courseKeys.length; j++)
                    {
                        //Build the value
                        var value = null;

                        //Check for all
                        if(this.courses[courseKeys[j]].employees && this.courses[courseKeys[j]].employees[this.employees[i].EmployeeId])
                        {
                            //Set it
                            value = this.courses[courseKeys[j]].employees[this.employees[i].EmployeeId].completed_at;

                            //Check for expires
                            if(this.courses[courseKeys[j]].employees[this.employees[i].EmployeeId].expires_at)
                            {
                                //Append
                                value += ' - '+ this.courses[courseKeys[j]].employees[this.employees[i].EmployeeId].expires_at;
                            }
                        }

                        //Set it
                        emp[this.courses[courseKeys[j]].course_name] = value;
                    }

                    //Push
                    items.push(emp);
                }

                //Build it
                return items;
            },

            divisionOptions(){
                //SEtup
                var options = [];

                //Loop and build
                for (var i = 0; i < this.divisions.length; i++)
                {
                    options.push({id: this.divisions[i].DivisionId, name: this.divisions[i].division_code + ' - ' + this.divisions[i].DivisionName});
                }

                //Send it
                return options;
            },

            tradeOptions(){
                //SEtup
                var options = [];

                //Loop and build
                for (var i = 0; i < this.trades.length; i++)
                {
                    options.push({id: this.trades[i].trade, name: this.trades[i].trade+ (this.trades[i].position != null ? ' - '+this.trades[i].position : '')});
                }

                //Send it
                return options;
            },

            positionOptions(){
                //SEtup
                var options = [];

                //Loop and build
                for (var i = 0; i < this.positions.length; i++)
                {
                    options.push({id: this.positions[i], name: this.positions[i]});
                }

                //Send it
                return options;
            },

            courseOptions(){
                //SEtup
                var options = [];

                //Loop and build
                for (var i = 0; i < this.courseFilters.length; i++)
                {
                    options.push({id: this.courseFilters[i].id, name: this.courseFilters[i].course_name});
                }

                //Send it
                return options;
            },

            catalogOptions(){
                //SEtup
                var options = [];

                //Loop and build
                for (var i = 0; i < this.catalogFilters.length; i++)
                {
                    options.push({id: this.catalogFilters[i].id, name: this.catalogFilters[i].catalog_name});
                }

                //Send it
                return options;
            },

            dateDisplayOptions(){
                //Sendit
                return [
                    {value: null, text: '-- Select Export Date Display (Defaults to Combined) --'},
                    {value: 'combined', text: 'Combined - Both Completed & Expire Dates Displayed in the same Cell'},
                    {value: 'separate', text: 'Separate - Completed & Expire Dates Displayed in Separate Cells'},
                ];
            },

            completedTypeOptions(){
                //Sendit
                return [
                    {value: null, text: '-- Include All Employees, Respective of Other Filters (Defaults to All) --'},
                    {value: 'all', text: 'All - Includes All Employees'},
                    {value: 'completed', text: 'Completed - Only Inludes Employees with Completed Courses'},
                ];
            },

            activeOptions(){
                //Sendit
                return [
                    {value: null, text: '-- Active/Inactive --'},
                    {value: 1, text: 'Active Only'},
                    {value: 0, text: 'Inactive Only'},
                ];
            },

            sortByOptions(){
                //Sendit
                return [
                    {id: 'employee.LastName', name: 'Employee Last Name'},
                    {id: 'employee.EmployeeNumber', name: 'Employee#'},
                    {id: 'division.division_code', name: 'Division Code'},
                    {id: 'division.DivisionName', name: 'Division Name'},
                    {id: 'employee.position', name: 'Position'},
                    {id: 'employee.trade', name: 'Trade'},
                ];
            },

            tradeOrPositionOptions(){
                //Sendit
                return [
                    {id: 'position', name: 'Position'},
                    {id: 'trade', name: 'Trade'},
                ];
            },
        },

        methods: {
            getMatrix(loadResources = true, exportFile = false) {
                //Setup
                //this.filters.load_resources = loadResources;
                //this.filters.export = exportFile;
                this.loading = true;
                this.has_ran = true;

                //Paylod
                var payload = {
                    load_resources: loadResources,
                    export: exportFile,
                    division_id: [],
                    trade: [],
                    positions: [],
                    training_course_id: [],
                    training_catalog_id: [],
                    employee_number: this.filters.employee_number,
                    last_name: this.filters.last_name,
                    //position: this.filters.position,
                    IsActive: this.filters.IsActive,
                    completed_at: this.filters.completed_at,
                    expires_at: this.filters.expires_at,
                    sort_by: (this.filters.sort_by ? this.filters.sort_by[0].id : null),
                    display_trade_or_position: (this.filters.display_trade_or_position ? this.filters.display_trade_or_position[0] : null),
                    date_display: this.filters.date_display,
                    completed_at_least_one: this.filters.completed_at_least_one,
                };

                //Loop and build
                for (var i = this.filters.division_id.length - 1; i >= 0; i--)
                {
                    payload.division_id.push(this.filters.division_id[i].id);
                }

                //Loop and build
                for (var i = this.filters.trade.length - 1; i >= 0; i--)
                {
                    payload.trade.push(this.filters.trade[i].id);
                }

                //Loop and build
                for (var i = this.filters.training_course_id.length - 1; i >= 0; i--)
                {
                    payload.training_course_id.push(this.filters.training_course_id[i].id);
                }

                //Loop and build
                for (var i = this.filters.training_catalog_id.length - 1; i >= 0; i--)
                {
                    payload.training_catalog_id.push(this.filters.training_catalog_id[i].id);
                }

                //Loop and build
                for (var i = this.filters.training_catalog_id.length - 1; i >= 0; i--)
                {
                    payload.training_catalog_id.push(this.filters.training_catalog_id[i].id);
                }

                //Loop and build
                for (var i = this.filters.positions.length - 1; i >= 0; i--)
                {
                    payload.positions.push(this.filters.positions[i].id);
                }

                //Get the data
                this.$http
                    .get('/training-courses/matrix', payload)
                    .then(response => {
                        //Check if
                        if(exportFile == true)
                        {
                            //window.location = '/files/download/export/'+response.data;
                            this.$toastr.success(response.data);
                        }
                        else
                        {
                            //Set the data
                            this.employees = response.data.employees;
                            this.courses = response.data.courses;

                            //Check for load resources
                            if(loadResources == true)
                            {
                                this.trades = response.data.trades;
                                this.divisions = response.data.divisions;
                                this.positions = response.data.positions;
                            }
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getResources() {
                //Setup
                //this.filters.load_resources = loadResources;
                //this.filters.export = exportFile;
                this.loading = true;

                //Paylod
                var payload = {
                    load_resources: true,
                };

                //Get the data
                this.$http
                    .get('/training-courses/matrix', payload)
                    .then(response => {
                        //Set the helpers
                        this.trades = response.data.trades;
                        this.divisions = response.data.divisions;
                        this.positions = response.data.positions;
                        this.courseFilters = response.data.courseFilters;
                        this.catalogFilters = response.data.catalogFilters;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },
        },

        mounted() {
            //Get the Training Course
            //this.getMatrix();
            this.getResources();
        }
    }
</script>

<style type="text/css" scoped>

</style>